import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Plus, Edit, Package, Monitor } from 'lucide-react'
import { toast } from 'sonner'
import z from 'zod'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

// Componentes UI Globales
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import { FormError } from '../components/ui/FormError'
import { SearchFilter } from '../components/ui/SearchFilter'
import { DiscreteLoader } from '../components/ui/discrete-loader'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { ConfirmationModal } from '../components/ui/ConfirmationModal'
import { Checkbox } from '../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '../components/ui/dialog'
import { CatalogSelect } from '../components/ui/CatalogSelect'

// Servicios
import { cashRegisterService, CashRegisterItem } from '../services/cash-register.service'
import { sucursalService, Sucursal } from '../services/sucursal.service'

// ─── 2. Esquema de Validación (Zod) ──────────────────────────────────────────
const formSchema = z.object({
  name: z.string().min(1, 'El nombre es obligatorio').max(255, 'Demasiado largo'),
  code: z
    .string()
    .min(1, 'El código es obligatorio')
    .max(50, 'Demasiado largo')
    .regex(/^[A-Z0-9_-]+$/, 'Solo mayúsculas, números, guiones o guiones bajos'),
  sucursal_id: z.number().min(1, 'La sucursal es obligatoria'),
  cash_limit_amount: z
    .number({ message: 'El límite de efectivo es obligatorio' })
    .positive('El límite debe ser mayor a cero')
    .max(999999999999.99, 'El límite es demasiado alto'),
  is_active: z.boolean(),
})

type FormValues = z.infer<typeof formSchema>

// ─── 3. Componente Principal ───────────────────────────────────────────────
export function CashRegisters() {
  const queryClient = useQueryClient()

  // ─── Estados ───
  const [data, setData] = useState<CashRegisterItem[]>([])
  const [paginationMeta, setPaginationMeta] = useState<any>({ total: 0, from: 0, to: 0 })
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [refreshCount, setRefreshCount] = useState(0)

  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)

  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // ─── Formulario (React Hook Form) ───
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema) as any,
    mode: 'onChange',
    defaultValues: { name: '', code: '', sucursal_id: 0, cash_limit_amount: 5000, is_active: true }
  })

  const isActiveValue = watch('is_active')

  // ─── Cargar Datos de la API ───
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await cashRegisterService.getItems({
          search: searchQuery,
          page: currentPage,
          per_page: itemsPerPage
        })

        const items = response.data?.data || response.data || []
        setData(items)
        setPaginationMeta(response.data || response.meta || response || { total: 0, from: 0, to: 0 })
      } catch (error) {
        toast.error('Error al cargar los datos')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [refreshCount, searchQuery, currentPage])

  const paginatedData = data

  // ─── Funciones de Interfaz ───
  const openCreateModal = () => {
    setEditingId(null)
    reset({ name: '', code: '', sucursal_id: 0, cash_limit_amount: 5000, is_active: true })
    setShowModal(true)
  }

  const openEditModal = (item: CashRegisterItem) => {
    setEditingId(item.id)
    reset({
      name: item.name,
      code: item.code,
      sucursal_id: item.sucursal_id,
      cash_limit_amount: Number(item.cash_limit_amount),
      is_active: item.is_active
    })
    setShowModal(true)
  }

  // ─── Funciones API ───
  const onSubmit = async (formData: FormValues) => {
    try {
      if (editingId) {
        // sucursal_id está prohibido en el endpoint PUT (no se puede cambiar)
        const { sucursal_id: _omitted, ...updatePayload } = formData
        await cashRegisterService.updateItem(editingId, updatePayload)
        toast.success('Caja actualizada exitosamente')
      } else {
        await cashRegisterService.createItem(formData)
        toast.success('Caja creada exitosamente')
      }
      await queryClient.invalidateQueries({ queryKey: ['register-context'] })
      setShowModal(false)
      setRefreshCount((prev) => prev + 1)
    } catch (error: any) {
      if (error.response?.data?.errors?.code === 'CASH_REGISTER_LIMIT_EXCEEDED') {
        toast.error(error.response?.data?.message || 'El límite no puede ser menor al efectivo actual de la caja')
      } else if (Array.isArray(error.response?.data?.errors?.code)) {
        toast.error('El código ingresado ya está en uso en esta sucursal o es inválido')
      } else {
        toast.error(error.response?.data?.message || 'Error al guardar la caja')
      }
    }
  }

  // ─── Renderizado (JSX) ───────────────────────────────────────────────────
  return (
    <div className="space-y-6 p-6">
      {/* 1. Header y Buscador */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Cajas</h1>
          <p className="mt-1 text-sm text-slate-600">
            Gestiona las cajas registradoras de cada sucursal.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-64">
            <SearchFilter
              placeholder="Buscar caja..."
              onSearch={(text: string) => {
                setSearchQuery(text)
                setCurrentPage(1)
              }}
            />
          </div>
          <Button onClick={openCreateModal} className="bg-primary hover:bg-primary/90">
            <Plus className="mr-2 h-4 w-4" /> Nueva Caja
          </Button>
        </div>
      </div>

      {/* 2. Tabla de Datos */}
      {isLoading ? (
        <TableSkeleton
          headers={['Nombre', 'Código', 'Sucursal', 'Límite de efectivo', 'Estado', 'Acciones']}
          columns={['text', 'text', 'text', 'text', 'badge', 'actions']}
          rows={5}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-slate-200 bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Nombre
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Código
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Sucursal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Límite de efectivo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Estado
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {paginatedData.length > 0 ? (
                  paginatedData.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-slate-900">
                        {item.name}
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        {item.code}
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        {item.sucursal?.name || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-slate-700">
                        {Number(item.cash_limit_amount).toLocaleString('es-MX', {
                          style: 'currency',
                          currency: 'MXN',
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <Badge
                          className={
                            item.is_active
                              ? 'bg-green-100 text-green-700 hover:bg-green-100'
                              : 'bg-red-100 text-red-700 hover:bg-red-100'
                          }
                        >
                          {item.is_active ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditModal(item)}
                            className="rounded p-1 hover:bg-slate-100 transition-colors"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4 text-slate-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <Monitor className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                      <p className="text-slate-500">No se encontraron cajas registradoras.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 3. Paginación */}
          {paginationMeta.total > 0 && (
            <GlobalPaginator
              totalItems={paginationMeta.total}
              itemsPerPage={itemsPerPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              from={paginationMeta.from}
              to={paginationMeta.to}
            />
          )}
        </div>
      )}

      {/* 4. Modal Crear/Editar */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar Caja' : 'Nueva Caja'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Nombre <span className="text-red-500">*</span>
              </label>
              <Input
                {...register('name')}
                placeholder="ej. Caja Principal"
              />
              {errors.name && <FormError message={errors.name.message} />}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Código <span className="text-red-500">*</span>
              </label>
              <Input
                {...register('code', {
                  setValueAs: (v) => (typeof v === 'string' ? v.toUpperCase() : v),
                  onChange: (e) => {
                    e.target.value = e.target.value.toUpperCase()
                  }
                })}
                placeholder="ej. CAJA_01"
                className="font-mono text-sm"
              />
              {errors.code && <FormError message={errors.code.message} />}
              <p className="mt-1 text-xs text-slate-500">
                Identificador único (solo mayúsculas, números y guiones).
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Límite de efectivo <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                min="0.01"
                max="999999999999.99"
                step="0.01"
                {...register('cash_limit_amount', { valueAsNumber: true })}
                placeholder="5000.00"
              />
              {errors.cash_limit_amount && <FormError message={errors.cash_limit_amount.message} />}
              <p className="mt-1 text-xs text-slate-500">
                Máximo de efectivo físico permitido en esta caja.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">
                Sucursal <span className="text-red-500">*</span>
                {editingId && (
                  <span className="ml-2 text-xs font-normal text-slate-400">(no se puede cambiar)</span>
                )}
              </label>
              <Controller
                name="sucursal_id"
                control={control}
                render={({ field }) => (
                  <CatalogSelect
                    catalog="sucursales"
                    placeholder="Seleccione una sucursal"
                    value={field.value}
                    onChange={(val: any) => field.onChange(val ? Number(val) : 0)}
                    disabled={!!editingId}
                  />
                )}
              />
              {errors.sucursal_id && <FormError message={errors.sucursal_id.message} />}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <Checkbox
                id="is_active"
                checked={isActiveValue}
                onCheckedChange={(val) =>
                  setValue('is_active', val as boolean, { shouldValidate: true })
                }
              />
              <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                Estado Activo
              </label>
            </div>

            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-primary hover:bg-primary/90"
              >
                {isSubmitting ? <DiscreteLoader /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
